import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, CheckCircle, XCircle, Clock, Shield, MapPin, Package, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const statusColors = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30",
  verified: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30",
  rejected: "text-red-400 bg-red-400/10 border-red-400/30",
};

export default function AdminSuppliers() {
  const { user } = useAuth();
  const isDemo = user?.is_demo === true;
  const [suppliers, setSuppliers] = useState([]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/suppliers-list`, { withCredentials: true });
      setSuppliers(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const verify = async (id, status, score) => {
    await axios.put(`${API}/admin/suppliers/${id}/verify`, { verification_status: status, trust_score: score }, { withCredentials: true });
    fetchSuppliers();
  };

  return (
    <div data-testid="admin-suppliers">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-['Outfit'] text-2xl font-bold text-white">Supplier Verification</h1>
        <span className="text-[#A1B2A8]/50 text-xs">{suppliers.length} suppliers</span>
      </div>

      <div className="space-y-3">
        {suppliers.map((s) => (
          <div key={s.id} className="bg-[#09120D] border border-[#1A2E22] p-6 hover:border-[#22C55E]/30 transition-colors" data-testid={`supplier-${s.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-white font-['Outfit'] font-semibold">{s.name}</h3>
                  <span className={`text-xs capitalize px-2 py-0.5 border ${statusColors[s.verification_status] || statusColors.pending}`}>{s.verification_status}</span>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5 text-[#A1B2A8] text-xs">
                    <MapPin className="w-3 h-3" /> {s.country}
                  </div>
                  <div className="flex items-center gap-1.5 text-[#A1B2A8] text-xs">
                    <Package className="w-3 h-3" /> {s.products.join(", ")}
                  </div>
                </div>
                <p className="text-[#A1B2A8]/60 text-xs mt-1">{s.email}</p>
              </div>

              <div className="flex items-center gap-4 flex-shrink-0">
                {s.trust_score > 0 && (
                  <div className="text-right">
                    <p className="text-[#A1B2A8] text-xs">Trust Score</p>
                    <div className="flex items-center gap-1">
                      <Shield className={`w-4 h-4 ${s.trust_score >= 90 ? "text-[#22C55E]" : s.trust_score >= 70 ? "text-yellow-400" : "text-red-400"}`} />
                      <span className="font-['Outfit'] text-lg font-bold text-white">{s.trust_score}</span>
                    </div>
                  </div>
                )}
                {isDemo ? (
                  <span className="flex items-center gap-1.5 text-[#22C55E] text-xs border border-[#22C55E]/30 bg-[#22C55E]/5 px-3 py-1.5" data-testid={`supplier-readonly-${s.id}`}>
                    <Eye className="w-3 h-3" /> Read-only
                  </span>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-[#A1B2A8] hover:text-white text-xs border border-[#1A2E22] px-3 py-1.5 flex items-center gap-1" data-testid={`supplier-action-${s.id}`}>
                        Action <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#09120D] border-[#1A2E22] text-white">
                      <DropdownMenuItem onClick={() => verify(s.id, "verified", 85)} className="text-[#22C55E] focus:bg-[#22C55E]/10 cursor-pointer"><CheckCircle className="w-3.5 h-3.5 mr-2" />Verify (Score: 85)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => verify(s.id, "verified", 95)} className="text-[#22C55E] focus:bg-[#22C55E]/10 cursor-pointer"><Shield className="w-3.5 h-3.5 mr-2" />Verify (Score: 95)</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => verify(s.id, "pending", 0)} className="text-yellow-400 focus:bg-yellow-400/10 cursor-pointer"><Clock className="w-3.5 h-3.5 mr-2" />Set Pending</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => verify(s.id, "rejected", 0)} className="text-red-400 focus:bg-red-400/10 cursor-pointer"><XCircle className="w-3.5 h-3.5 mr-2" />Reject</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
